import React, { useEffect, useState } from 'react';
import api from '../../config/axios';
import './OrderCreate.css';
import Header from '../common/Header';
import { useNavigate, useLocation } from 'react-router-dom';
import { useShop } from '../../contexts/ShopContext';
import { itemTotal, orderSubtotal, orderTax, orderTotal, taxRateLabel } from '../../utils/tax';

interface MenuItem {
  _id: string;
  name: string;
  price: number;
  description?: string;
  imageUrl: string;
  // Thuế thực áp cho món (backend đã gộp cả mức mặc định của shop).
  effectiveTaxRate?: number;
}

interface OrderItem {
  menuId: string;
  name: string;
  price: number;
  quantity: number;
  // Thuế suất của món, chỉ để hiển thị — backend luôn chốt lại theo menu.
  taxRate: number;
}

interface OrderCreateProps {
  user: any;
}

const OrderCreate: React.FC<OrderCreateProps> = ({ user }) => {
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [orderHistory, setOrderHistory] = useState<any[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const navigate = useNavigate();
  const location = useLocation();
  // Thuế mặc định của shop, dùng cho món chưa đặt thuế riêng. Nhân viên chỉ xem.
  const { taxRate: shopTaxRate } = useShop();
  // Giữ nguyên chuỗi người dùng gõ; ép về số chỉ khi tính toán. Nếu bind thẳng
  // state số vào input, xóa trắng ô sẽ bị Number('') kéo về 0 và không gõ tiếp được.
  const [discountInput, setDiscountInput] = useState<string>('');
  const discount = Math.max(0, Number(discountInput) || 0);
  const [tables, setTables] = useState<{_id: string, name: string, seats: number, status: string}[]>([]);
  const [tableId, setTableId] = useState<string>('');

  useEffect(() => {
    if (location.state && Array.isArray(location.state.items) && location.state.items.length > 0) {
      setOrderItems(location.state.items.map((item: any) => ({
        menuId: item.menuId || item._id || '',
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        imageUrl: item.imageUrl || '',
        taxRate: Number(item.taxRate ?? item.effectiveTaxRate ?? shopTaxRate) || 0
      })));
    }
  }, []);

  const fetchOrderHistory = async () => {
    try {
      const res = await api.get('/api/orders?staffId=' + user.id);
      setOrderHistory(res.data.data);
    } catch (err) {
      setOrderHistory([]);
    }
  };

  const filteredOrderHistory = selectedDate
    ? orderHistory.filter(order => {
        const orderDate = new Date(order.createdAt).toISOString().split('T')[0];
        return orderDate === selectedDate;
      })
    : orderHistory;

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
  };

  const handleShowAll = () => {
    setSelectedDate('');
  };

  const fetchTables = async () => {
    try {
      const res = await api.get('/api/tables');
      setTables(res.data);
    } catch (err) {
      setTables([]);
    }
  };

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        setLoading(true);
        const res = await api.get('/api/menu');
        setMenu(res.data.data);
      } catch (err) {
        setMenu([]);
      } finally {
        setLoading(false);
      }
    };
    const fetchReservationTable = async () => {
      if (location.state && location.state.reservationId) {
        try {
          const res = await api.get('/api/tables/reservations');
          const reservation = res.data.find((r: any) => r._id === location.state.reservationId);
          if (reservation && reservation.table && typeof reservation.table === 'string') {
            setTableId(reservation.table);
          } else if (reservation && reservation.table && reservation.table._id) {
            setTableId(reservation.table._id);
          }
        } catch (err) {
        }
      }
    };
    fetchMenu();
    fetchOrderHistory();
    fetchTables();
    fetchReservationTable();
  }, [user.id]);

  const handleAddItem = (item: MenuItem) => {
    setOrderItems(prev => {
      const found = prev.find(i => i.menuId === item._id);
      if (found) {
        return prev.map(i => i.menuId === item._id ? { ...i, quantity: i.quantity + 1 } : i);
      } else {
        return [...prev, { menuId: item._id, name: item.name, price: item.price, quantity: 1, imageUrl: item.imageUrl, taxRate: Number(item.effectiveTaxRate ?? shopTaxRate) || 0 }];
      }
    });
  };

  const handleChangeQty = (menuId: string, qty: number) => {
    setOrderItems(prev => prev.map(i => i.menuId === menuId ? { ...i, quantity: qty } : i));
  };

  const handleRemoveItem = (menuId: string) => {
    setOrderItems(prev => prev.filter(i => i.menuId !== menuId));
  };

  // Thuế của đơn = tổng thuế từng món (mỗi món có thuế suất riêng).
  const subtotal = orderSubtotal({ items: orderItems });
  const tax = orderTax({ items: orderItems });

  const handleSubmit = async () => {
    setError(''); setSuccess('');
    if (orderItems.length === 0) {
      setError('Vui lòng chọn ít nhất 1 món!');
      return;
    }
    if (!tableId) {
      setError('Vui lòng chọn bàn!');
      return;
    }
    try {
      setLoading(true);
      const reservationId = location.state && location.state.reservationId ? location.state.reservationId : undefined;
      
      const response = await api.post('/api/orders', {
        items: orderItems,
        discount,
        reservationId,
        tableId
      });
      
      const createdOrder = response.data.data;
      
      setSuccess('Tạo đơn hàng thành công! Chuyển đến trang thanh toán...');
      
      setTimeout(() => {
        navigate('/orders/payment', { 
          state: { 
            orderId: createdOrder._id,
            orderData: createdOrder,
            fromCreate: true
          } 
        });
      }, 1500);
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lỗi khi tạo đơn hàng');
    } finally {
      setLoading(false);
    }
  };

  const onLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  return (
    <>
      <Header user={user} onLogout={onLogout}>
        <span className="user-role">{user.role === 'admin' ? 'Quản lý' : 'Nhân viên'}</span>
      </Header>
      <div className="order-create-bg">
        <div className="order-create-container">
          <div className="order-create-header">
            <button
              className="order-create-back-btn"
              onClick={() => navigate('/')}
              title="Quay lại Dashboard"
            >
              ←
            </button>
            <h2 className="order-create-title">TẠO ĐƠN HÀNG MỚI</h2>
          </div>
          
          {error && <div className="order-create-message order-create-error">{error}</div>}
          {success && <div className="order-create-message order-create-success">{success}</div>}
          {loading && <div className="order-create-loading">Đang tải...</div>}
          
          <div className="order-create-main">
            <div className="order-create-table-select-group">
              <label className="order-create-input-label">Chọn bàn</label>
              <select
                className="order-create-input"
                value={tableId}
                onChange={e => setTableId(e.target.value)}
                disabled={!!(location.state && location.state.reservationId)}
              >
                <option value="">-- Chọn bàn --</option>
                {(() => {
                  let reservationTableId = '';
                  if (location.state && location.state.reservationId && tableId) {
                    reservationTableId = tableId;
                  }
                  const reservationTable = tables.find(t => t._id === reservationTableId);
                  const availableTables = tables.filter(t => t.status === 'available' && t._id !== reservationTableId);
                  return [
                    reservationTable && (
                      <option key={reservationTable._id} value={reservationTable._id}>
                        {reservationTable.name} ({reservationTable.seats} ghế){reservationTable.status !== 'available' ? ' - Đã đặt' : ''}
                      </option>
                    ),
                    ...availableTables.map(table => (
                      <option key={table._id} value={table._id}>
                        {table.name} ({table.seats} ghế)
                      </option>
                    ))
                  ];
                })()}
              </select>
            </div>
            <div className="order-create-left">
              <h3 className="order-create-section-title">Menu</h3>
              <div className="order-create-menu-list">
                {menu.map(item => (
                  <div key={item._id} className="order-create-menu-item">
                    <img src={item.imageUrl} alt={item.name} className="order-create-menu-img" />
                    <div className="order-create-menu-info">
                      <div className="order-create-menu-name">{item.name}</div>
                      <div className="order-create-menu-price">{item.price.toLocaleString()} đ</div>
                      <div className="order-create-menu-tax">Thuế {(item.effectiveTaxRate ?? shopTaxRate ?? 0).toLocaleString('vi-VN')}%</div>
                    </div>
                    <button onClick={() => handleAddItem(item)} className="order-create-menu-add-btn">Thêm</button>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="order-create-right">
              <h3 className="order-create-section-title">Đơn hàng</h3>
              {orderItems.length === 0 ? (
                <div className="order-create-empty">Chưa có món nào.</div>
              ) : (
                <table className="order-create-table">
                  <thead>
                    <tr>
                      <th>Món</th>
                      <th>Giá</th>
                      <th>Thuế</th>
                      <th>Số lượng</th>
                      <th>Thành tiền</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                  {orderItems.map(item => {
                    const itemSubtotal = item.price * item.quantity;
                    // Giảm giá vẫn chia đều theo tỉ trọng tiền món; thuế thì theo món.
                    const itemDiscount = subtotal > 0 ? (itemSubtotal / subtotal) * discount : 0;
                    const lineTotal = itemTotal(item) - itemDiscount;

                    return (
                      <tr key={item.menuId}>
                        <td>{item.name}</td>
                        <td>{item.price.toLocaleString()} đ</td>
                        <td>{item.taxRate.toLocaleString('vi-VN')}%</td>
                        <td>
                          <input
                            type="number"
                            min={1}
                            value={item.quantity}
                            onChange={e => handleChangeQty(item.menuId, Math.max(1, Number(e.target.value)))}
                            className="order-create-quantity-input"
                          />
                        </td>
                        <td>{Math.round(lineTotal).toLocaleString()} đ</td>
                        <td>
                          <button onClick={() => handleRemoveItem(item.menuId)} className="order-create-remove-btn">X</button>
                        </td>
                      </tr>
                    );
                  })}
                  </tbody>
                </table>
              )}
              
              {orderItems.length > 0 && (
              <div className="order-create-inputs-group">
                <div className="order-create-input-group">
                  <label className="order-create-input-label">Thuế ({taxRateLabel({ items: orderItems })})</label>
                  <input
                    type="text"
                    value={`${Math.round(tax).toLocaleString()} đ`}
                    disabled
                    readOnly
                    className="order-create-input"
                    title="Thuế tính theo từng món; quản lý cấu hình trong Menu"
                  />
                </div>
                <div className="order-create-input-group">
                  <label className="order-create-input-label">Giảm giá (đ)</label>
                  <input
                    type="number"
                    min={0}
                    value={discountInput}
                    onChange={e => setDiscountInput(e.target.value)}
                    className="order-create-input"
                  />
                </div>
              </div>
            )}
              
              <button onClick={handleSubmit} className="order-create-submit-btn" disabled={loading}>
                {loading ? 'Đang xử lý...' : 'Tạo đơn'}
              </button>
            </div>
          </div>
          
          <div className="order-create-history-section">
            <h3 className="order-create-history-title">Lịch sử đơn hàng của bạn</h3>
            <div className="order-create-history-filters">
              <input
                type="date"
                value={selectedDate}
                onChange={e => handleDateChange(e.target.value)}
                className="order-create-history-date-input"
              />
              <button
                onClick={handleShowAll}
                className={`order-create-history-filter-btn${selectedDate ? ' active' : ''}`}
              >
                {selectedDate ? 'Hiển thị tất cả' : 'Lọc theo ngày'}
              </button>
            </div>
            
            {filteredOrderHistory.length === 0 ? (
              <div className="order-create-empty">Chưa có đơn hàng nào.</div>
            ) : (
              <table className="order-create-history-table">
                <thead>
                  <tr>
                    <th>Mã đơn</th>
                    <th>Thời gian</th>
                    <th>Bàn</th>
                    <th>Tổng tiền</th>
                    <th>Số món</th>
                    <th>Trạng thái</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrderHistory.map(order => (
                    <tr key={order._id} onClick={() => setSelectedOrder(order)}>
                      <td>{order._id.slice(-6).toUpperCase()}</td>
                      <td>{new Date(order.createdAt).toLocaleString()}</td>
                      <td>{
                        order.mergedTables && order.mergedTables.length > 0 
                          ? order.mergedTables.map((table: any) => table.name).join(', ')
                          : order.table?.name || 
                            order.tableId?.name || 
                            (typeof order.tableId === 'string' ? order.tableId : 'Không xác định')
                      }</td>
                      <td>{order.total.toLocaleString()} đ</td>
                      <td>{order.items.length}</td>
                      <td>{order.status === 'pending' ? 'Chờ thanh toán' : order.status === 'paid' ? 'Đã thanh toán' : order.status === 'cancelled' ? 'Đã hủy' : order.status === 'merged' ? 'Đã gộp' : order.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            
            {selectedOrder && (
              <div className="order-create-detail-modal">
                <div className="order-create-detail-content">
                  <div className="order-create-detail-header">
                    <h3 className="order-create-detail-title">Chi tiết đơn hàng #{selectedOrder._id.slice(-6).toUpperCase()}</h3>
                    <button 
                      className="order-create-detail-close-btn"
                      onClick={() => setSelectedOrder(null)}
                    >
                      ×
                    </button>
                  </div>
                  
                  <div className="order-create-detail-info">
                    <div><b>Nhân viên:</b> {
                      selectedOrder.staffName || 
                      selectedOrder.staff?.username || 
                      selectedOrder.staffId?.username || 
                      (typeof selectedOrder.staffId === 'object' ? selectedOrder.staffId.username : selectedOrder.staffId) ||
                      'Không xác định'
                    }</div>
                    <div><b>Thời gian:</b> {new Date(selectedOrder.createdAt).toLocaleString()}</div>
                    <div><b>Bàn:</b> {
                      selectedOrder.mergedTables && selectedOrder.mergedTables.length > 0 
                        ? selectedOrder.mergedTables.map((table: any) => table.name).join(', ')
                        : selectedOrder.table?.name || 
                          selectedOrder.tableId?.name || 
                          (typeof selectedOrder.tableId === 'string' ? selectedOrder.tableId : 'Không xác định')
                    }</div>
                    <div><b>Tổng tiền:</b> {selectedOrder.total.toLocaleString()} ₫</div>
                    <div><b>Số món:</b> {selectedOrder.items.length}</div>
                  </div>
                  
                  <table className="order-create-detail-table">
                    <thead>
                      <tr>
                        <th>Ảnh</th>
                        <th>Tên món</th>
                        <th>Giá</th>
                        <th>Số lượng</th>
                        <th>Thành tiền</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items.map((item: any, idx: number) => {
                        const lineTotal = itemTotal(item, selectedOrder.taxRate || 0);
                        
                        return (
                          <tr key={idx}>
                            <td>
                              {item.imageUrl ? (
                                <img src={item.imageUrl} alt={item.name} className="order-create-detail-img" />
                              ) : (
                                <span style={{ color: '#aaa' }}>Không có</span>
                              )}
                            </td>
                            <td>{item.name}</td>
                            <td>{item.price.toLocaleString()} đ</td>
                            <td>{item.quantity}</td>
                            <td>{Math.round(lineTotal).toLocaleString()} đ</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="order-create-detail-summary">
                    {(() => {
                      const calculatedSubtotal = orderSubtotal(selectedOrder);
                      const calculatedTax = orderTax(selectedOrder);
                      const calculatedDiscount = selectedOrder.discount || 0;
                      const calculatedTotal = orderTotal(selectedOrder);
                      return (
                        <>
                          <div className="order-create-detail-summary-item">
                            <span>Tạm tính:</span>
                            <span>{calculatedSubtotal.toLocaleString()} đ</span>
                          </div>
                          <div className="order-create-detail-summary-item">
                            <span>Thuế ({taxRateLabel(selectedOrder)}):</span>
                            <span>{Math.round(calculatedTax).toLocaleString()} đ</span>
                          </div>
                          <div className="order-create-detail-summary-item">
                            <span>Giảm giá:</span>
                            <span>-{calculatedDiscount.toLocaleString()} đ</span>
                          </div>
                          <div className="order-create-detail-summary-item">
                            <span className="order-create-detail-summary-total">Tổng cộng:</span>
                            <span className="order-create-detail-summary-total">{calculatedTotal.toLocaleString()} đ</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  {(!selectedOrder.status || selectedOrder.status === 'pending') && (
                    <div className="order-create-detail-actions">
                      <button
                        className="order-create-detail-delete-btn"
                        onClick={async () => {
                          if (window.confirm('Bạn có chắc muốn xóa đơn này?')) {
                            try {
                              await api.post(`/api/orders/${selectedOrder._id}/cancel`, {});
                              setSelectedOrder(null);
                              fetchOrderHistory();
                            } catch (err) {
                              alert('Lỗi khi xóa đơn!');
                            }
                          }
                        }}
                      >
                        Xóa
                      </button>
                      <button
                        className="order-create-detail-pay-btn"
                        onClick={() => {
                          const orderId = selectedOrder._id;
                          setSelectedOrder(null);
                          // Sang trang thanh toán và chọn sẵn đúng đơn hàng vừa xem.
                          navigate('/orders/payment', { state: { orderId } });
                        }}
                      >
                        Thanh toán
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderCreate; 